import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, FileText, Bookmark, Upload, Check, Send, User, Bot, Eye, Plus } from "lucide-react";
import { runDeepDive, publishDeepDive, sendFollowUp } from "../lib/api";
import { isDeepDiveSaved, toggleDeepDiveSaved } from "../lib/bookmarks";
import { renderMarkdown } from "../lib/markdown";
import useLiveViewers from "../hooks/useLiveViewers";
import { isAdmin } from "../lib/admin";
import { logSearchEvent, useTrackPageView } from "../hooks/useAnalytics";
import { useDossierProgress } from "../hooks/useDossierProgress";
import { celebrateNewSigils } from "../lib/sigilCelebration";
import UrlTranslator from "../components/UrlTranslator";
import PageHeader from "../components/PageHeader";
import QuestionMode from "../components/QuestionMode";
import { toast } from "sonner";

const DRAFT_KEY = "vault.deepdive.draft.v1";
const ACTIVE_THREAD_KEY = "vault.deepdive.active.v1";

const loadDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};
const saveDraft = (d) => {
  if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  else localStorage.removeItem(DRAFT_KEY);
};
const loadActiveThread = () => {
  try { return JSON.parse(localStorage.getItem(ACTIVE_THREAD_KEY) || "null"); } catch { return null; }
};
const saveActiveThread = (t) => {
  if (t) localStorage.setItem(ACTIVE_THREAD_KEY, JSON.stringify(t));
  else localStorage.removeItem(ACTIVE_THREAD_KEY);
};

export default function DeepDivePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillTopic = searchParams.get("topic") || "";
  const initialDraft = loadDraft() || { topic: "", angle: "" };
  const initialThread = loadActiveThread();
  const [topic, setTopic] = useState(prefillTopic || initialDraft.topic || "");
  const [angle, setAngle] = useState(initialDraft.angle || "");
  const [skeptical, setSkeptical] = useState(!!initialDraft.skeptical);
  // Dive mode: "standard" investigative dossier · "eli5" plain-English explainer
  // for a complete beginner · "social" generates a content-creator post-prompt
  // list (TikTok/X/Reels/Shorts). Initial value comes from URL `?mode=` if
  // present so we can deep-link a mode picker (e.g. from the home page).
  const initialMode = (() => {
    const m = (searchParams.get("mode") || "").toLowerCase();
    return ["eli5", "social"].includes(m) ? m : (initialDraft.mode || "standard");
  })();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  // Real backend events (from polling). Passed to useDossierProgress for the live wait UI.
  const [realProgress, setRealProgress] = useState([]);
  const [thread, setThread] = useState(initialThread);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(initialThread ? isDeepDiveSaved(initialThread.id) : false);
  const [publishing, setPublishing] = useState(false);
  const [allowCosigns, setAllowCosigns] = useState(true);  // user-controlled at publish time
  const [followUp, setFollowUp] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const progressLines = useDossierProgress(loading, realProgress);
  // Tick once per second while loading so the header elapsed counter updates live without
  // re-rendering the whole component for every poll.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!loading) { setElapsedSec(0); return undefined; }
    const start = Date.now();
    const t = setInterval(() => setElapsedSec(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [loading]);
  useTrackPageView("/deep-dive");

  // If arriving with ?topic= prefill, clear it from URL after consuming
  useEffect(() => {
    if (prefillTopic && !thread) {
      const next = new URLSearchParams(searchParams);
      next.delete("topic");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist input draft as user types
  useEffect(() => {
    if (!thread) saveDraft({ topic, angle });
  }, [topic, angle, thread]);

  // Persist active thread so accidental nav doesn't lose it
  useEffect(() => {
    saveActiveThread(thread);
  }, [thread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [thread?.messages?.length, sending]);

  const startNew = () => {
    saveActiveThread(null);
    saveDraft(null);
    setThread(null);
    setTopic("");
    setAngle("");
    setErr(null);
    setSaved(false);
    setFollowUp("");
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!topic.trim()) return;
    setLoading(true);
    setErr(null);
    setThread(null);
    setRealProgress([]);
    logSearchEvent("search_deep_dive", topic.trim());
    try {
      const r = await runDeepDive(
        topic.trim(),
        angle.trim() || null,
        skeptical,
        (events) => setRealProgress(events),
        { mode },
      );
      setThread(r);
      setSaved(isDeepDiveSaved(r.id));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      const isTimeout = e?.code === "ECONNABORTED" || /timeout|502|gateway/i.test(detail);
      setErr(
        isTimeout
          ? "Research engine timed out or upstream gateway hiccup. Try again — it usually works on the second attempt."
          : detail || "Research engine failure."
      );
    } finally {
      setLoading(false);
    }
  };

  const onSave = () => {
    if (!thread) return;
    const next = toggleDeepDiveSaved(thread.id);
    setSaved(next);
    toast.success(next ? "Saved to your library" : "Removed from your library");
  };

  const onPublish = async () => {
    if (!thread || thread.is_published || publishing) return;
    setPublishing(true);
    try {
      const r = await publishDeepDive(thread.id, { cosignsAllowed: allowCosigns });
      setThread((prev) => ({ ...prev, is_published: r.is_published }));
      // Auto-bookmark on publish so sigil progress counts a published
      // dive without the user needing a second click.
      if (!isDeepDiveSaved(thread.id)) {
        toggleDeepDiveSaved(thread.id);
        setSaved(true);
      }
      toast.success("Published to the public archive");
      // Publishing a dive often unlocks sigils (deep_diver, skeptics_edge, etc.)
      celebrateNewSigils().catch(() => {});
    } catch (e) {
      toast.error("Publish failed — try again");
    } finally {
      setPublishing(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!followUp.trim() || !thread || sending) return;
    const text = followUp.trim();
    setFollowUp("");
    setSending(true);

    // optimistic user msg
    const nowIso = new Date().toISOString();
    setThread((prev) => ({
      ...prev,
      messages: [...(prev.messages || []), { role: "user", content: text, timestamp: nowIso }],
    }));

    try {
      const updated = await sendFollowUp(thread.id, text);
      setThread(updated);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(detail || "Follow-up failed — try again");
      // rollback optimistic msg
      setThread((prev) => ({
        ...prev,
        messages: (prev.messages || []).slice(0, -1),
      }));
      setFollowUp(text); // restore input
    } finally {
      setSending(false);
    }
  };

  // ===== INITIAL FORM (no thread yet) =====
  if (!thread) {
    return (
      <div data-testid="deep-dive-page">
        <PageHeader
          color="#00ff88"
          label="AI Deep Dive"
          icon={<span className="w-1.5 h-1.5 bg-[#00ff88] rounded-full animate-pulse inline-block" />}
          title={<>The <span className="text-[#00ff88]">Research Engine.</span></>}
          subtitle={<>Spin up a Gemini agent on any subject — it compiles claims, counter-evidence, a timeline, and primary sources in seconds. Six modes from <strong className="text-[#00ff88]">Investigative</strong> and <strong className="text-[#00ff88]">ELI5</strong> to <strong className="text-[#00ff88]">URL Translator</strong>. Keep chatting to drill in, then publish to the archive.</>}
          testid="deep-dive-header"
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">

        <form onSubmit={submit} className="space-y-8 mb-10">
          {/* TARGET PANEL — Topic + Angle consolidated into a single solid card */}
          <section className="border border-[#ff3b30]/30 bg-[#0a0a0a]" data-testid="deep-dive-target-panel">
            <div className="px-5 py-3 border-b border-[#ff3b30]/30 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#ff3b30] rounded-full animate-pulse" />
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#ff3b30]">// TARGET</span>
              </div>
              <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
                What should the AI investigate?
              </span>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                  TOPIC
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Operation Gladio"
                  data-testid="deep-dive-topic-input"
                  className="w-full bg-[#050505] border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-[#ff3b30] focus:outline-none"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                  ANGLE — OPTIONAL FRAMING
                </label>
                <input
                  type="text"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder="e.g. focus on declassified NATO documents"
                  data-testid="deep-dive-angle-input"
                  className="w-full bg-[#050505] border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-[#ff3b30] focus:outline-none"
                  disabled={loading}
                />
              </div>
            </div>
          </section>

          {/* DIVE MODE PICKER */}
          <fieldset className="border border-white/15 bg-[#0a0a0a]" data-testid="dive-mode-picker">
            <legend className="ml-4 px-2 font-mono text-[10px] tracking-[0.3em] text-zinc-400">
              // DIVE MODE
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-4">
              {[
                {
                  id: "standard",
                  label: "INVESTIGATIVE DOSSIER",
                  desc: "Dense, sourced archive file — Executive Summary, Key Claims, Evidence, Timeline, Money Flow, and Open Threads with primary-source URLs. Default mode.",
                  accent: "#ff3b30",
                },
                {
                  id: "eli5",
                  label: "ELI5 — EXPLAIN LIKE I'M 5",
                  desc: "Same topic, written for someone who has NEVER heard of it. No jargon, plain language, with a 30-second version up top + 'Why You Should Care' + 'What People Get Wrong'. Built for normies/skeptics you're trying to bring up to speed.",
                  accent: "#00ff88",
                },
                {
                  id: "social",
                  label: "SOCIAL POST PROMPTS",
                  desc: "Skips the dossier and gives you a LIST of ready-to-use content angles: 8-10 TikTok/Reels/Shorts hooks, 5 X-thread starters, 4 Instagram-carousel outlines, 3 YouTube hook lines, and 6-8 quotable facts. Drop in a topic, get a week of post ideas.",
                  accent: "#ec4899",
                },
              ].map((m) => {
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMode(m.id)}
                    disabled={loading}
                    data-testid={`dive-mode-${m.id}`}
                    aria-pressed={active}
                    className={`text-left p-3 border transition-colors bg-[#050505] ${
                      active
                        ? ""
                        : "border-white/10 hover:border-white/30"
                    }`}
                    style={active ? { borderColor: `${m.accent}99`, boxShadow: `inset 0 0 0 1px ${m.accent}33` } : undefined}
                  >
                    <div
                      className="font-mono text-[10px] tracking-[0.15em] uppercase"
                      style={{ color: active ? m.accent : "#e4e4e7" }}
                    >
                      {active ? "● " : "○ "}{m.label}
                    </div>
                    <p className="text-[10px] text-zinc-400 leading-relaxed mt-1.5">{m.desc}</p>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {mode === "standard" && (
            <label
              className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors bg-[#0a0a0a] ${
                skeptical
                  ? "border-[#ff3b30]/60"
                  : "border-white/15 hover:border-white/30"
              }`}
              data-testid="skeptical-toggle"
            >
              <input
                type="checkbox"
                checked={skeptical}
                onChange={(e) => setSkeptical(e.target.checked)}
                className="mt-1 accent-[#ff3b30]"
                disabled={loading}
              />
              <div className="flex-1">
                <div className={`font-mono text-[11px] tracking-[0.15em] ${skeptical ? "text-[#ff3b30]" : "text-white"}`}>
                  SKEPTICAL MODE
                </div>
                <p className="text-[11px] text-zinc-400 leading-relaxed mt-1">
                  Force the AI to steelman the alternative narrative, scrutinize official explanations,
                  and surface inconvenient evidence the mainstream tends to gloss over. Still cites
                  real URLs only.
                </p>
              </div>
            </label>
          )}

          <UrlTranslator />

          <QuestionMode initialTopic={topic} />

          <button
            type="submit"
            disabled={loading || !topic.trim()}
            data-testid="deep-dive-submit"
            className="inline-flex items-center gap-2 bg-[#ff3b30] text-white hover:bg-[#f5f5f5] hover:text-[#050505] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Compiling Initial Dossier...
              </>
            ) : (
              <>
                <Sparkles size={14} /> Start Research Thread
              </>
            )}
          </button>
        </form>

        {err && (
          <div
            data-testid="deep-dive-error"
            className="border border-[#ff3b30]/50 bg-[#ff3b30]/5 p-5 mb-8 font-mono text-xs text-[#ff3b30]"
          >
            ERROR — {err}
          </div>
        )}

        {loading && (
          <div
            data-testid="dd-progress-shell"
            className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-8 font-mono text-xs text-zinc-400"
          >
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <span className="text-[10px] tracking-[0.25em] text-[#00ff88]">
                ◉ AGENT // ACTIVE — DO NOT CLOSE
              </span>
              <span className="text-[10px] tracking-[0.25em] text-zinc-600 tabular-nums">
                T+{elapsedSec.toString().padStart(2, "0")}s · POLLING /api/deep-dive
              </span>
            </div>
            <div className="space-y-1 leading-relaxed">
              {progressLines.map((line, i) => {
                const isLatest = i === progressLines.length - 1;
                const isReal = line.kind === "real";
                // Format delta: under 1s → ms, otherwise s with 1 decimal. Skip on first real event.
                const showDelta = isReal && line.deltaMs > 100;
                const deltaTxt = !showDelta ? "" : line.deltaMs < 1000
                  ? `+${line.deltaMs}ms`
                  : `+${(line.deltaMs / 1000).toFixed(1)}s`;
                return (
                  <div
                    key={i}
                    className={`flex gap-2 ${isLatest ? "text-white" : (isReal ? "text-zinc-300" : "text-zinc-500")}`}
                    data-testid={`dd-progress-line-${i}`}
                  >
                    <span className={isReal ? "text-[#00ff88]" : "text-zinc-700"}>
                      {isReal ? "●" : ">"}
                    </span>
                    <span className="whitespace-pre-wrap break-words flex-1 min-w-0">{line.label}</span>
                    {showDelta && (
                      <span className="text-zinc-600 text-[10px] tracking-[0.15em] tabular-nums whitespace-nowrap self-center">
                        ({deltaTxt})
                      </span>
                    )}
                    {isLatest && (
                      <span
                        className={`ml-1 inline-block w-1.5 h-3 animate-pulse self-end ${isReal ? "bg-[#00ff88]" : "bg-zinc-500"}`}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-3 border-t border-white/10 text-[10px] tracking-[0.25em] text-[#ffd60a]">
              &gt; usually 15–45 seconds · longer for niche topics · receipts will appear below
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

  // ===== THREAD MODE =====
  return (
    <div data-testid="deep-dive-thread" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <button
          onClick={startNew}
          data-testid="dd-new-thread"
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 hover:text-white border border-white/15 px-3 py-1.5 hover:border-white/40 transition-colors"
        >
          <Plus size={11} /> NEW THREAD
        </button>
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
          ID — {thread.id.slice(0, 8).toUpperCase()}
        </div>
      </div>

      <div className="bg-[#0F0F0F] border-t-4 border-t-[#00ff88] border-x border-b border-white/10 p-6 md:p-10 mb-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10 flex-wrap gap-3">
          <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            <FileText size={12} />
            DOSSIER — AI-COMPILED // {thread.messages.length} MESSAGES
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#00ff88]">
            ENGINE: gemini-3-flash
          </div>
        </div>

        <h2 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl text-white mb-6">
          {thread.topic}
        </h2>

        <div className="flex flex-wrap gap-3 mb-8 pb-6 border-b border-white/10">
          <button
            onClick={onSave}
            data-testid="dd-save-button"
            className={`inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
              saved
                ? "border-[#ffd60a]/50 text-[#ffd60a] bg-[#ffd60a]/5"
                : "border-white/20 text-zinc-300 hover:text-[#ffd60a] hover:border-[#ffd60a]/50"
            }`}
          >
            <Bookmark size={12} fill={saved ? "#ffd60a" : "none"} />
            {saved ? "Saved" : "Save to My Library"}
          </button>
          <button
            onClick={onPublish}
            disabled={thread.is_published || publishing}
            data-testid="dd-publish-button"
            className={`inline-flex items-center gap-2 px-4 py-2 border font-mono text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-100 ${
              thread.is_published
                ? "border-[#00ff88]/50 text-[#00ff88] bg-[#00ff88]/5 cursor-default"
                : publishing
                ? "border-white/20 text-zinc-400"
                : "border-white/20 text-zinc-300 hover:text-[#00ff88] hover:border-[#00ff88]/50"
            }`}
          >
            {thread.is_published ? <Check size={12} /> : publishing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {thread.is_published ? "Published" : publishing ? "Publishing..." : "Publish to Public Archive"}
          </button>
          {/* Cosigns gate — author choice at publish time. Hidden after publishing
              since it's locked in at that moment. */}
          {!thread.is_published && (
            <label
              className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white hover:border-white/30 transition-colors cursor-pointer"
              data-testid="dd-cosigns-toggle"
            >
              <input
                type="checkbox"
                checked={allowCosigns}
                onChange={(e) => setAllowCosigns(e.target.checked)}
                className="accent-[#00ff88]"
                data-testid="dd-cosigns-checkbox"
              />
              Allow Co-signs
            </label>
          )}
        </div>

        {/* MESSAGE THREAD */}
        <div className="space-y-6">
          {thread.messages?.map((m, i) => {
            // Don't render generating/error placeholders inline — the spinner block below covers them.
            if (m.role === "assistant" && m.status && m.status !== "done") return null;
            return (
            <div
              key={i}
              data-testid={`thread-message-${i}`}
              className={m.role === "user" ? "border-l-2 border-[#ff3b30] pl-5" : "border-l-2 border-[#00ff88] pl-5"}
            >
              <div className={`font-mono text-[10px] tracking-[0.25em] mb-3 flex items-center gap-2 ${m.role === "user" ? "text-[#ff3b30]" : "text-[#00ff88]"}`}>
                {m.role === "user" ? <><User size={11} /> RESEARCHER</> : <><Bot size={11} /> ANALYST</>}
              </div>
              {m.role === "user" ? (
                <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.content}</p>
              ) : (
                <div
                  className="markdown text-zinc-300"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
                />
              )}
            </div>
            );
          })}
          {sending && (
            <div className="border-l-2 border-[#00ff88]/50 pl-5">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#00ff88] mb-3 flex items-center gap-2">
                <Bot size={11} /> ANALYST
              </div>
              <div className="text-zinc-500 font-mono text-xs flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                Compiling response...
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* FOLLOW-UP INPUT */}
      <form onSubmit={sendMessage} className="bg-[#0a0a0a] border border-white/10 p-4 sticky bottom-4">
        <div className="flex items-end gap-3">
          <textarea
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            placeholder="Ask a follow-up... (Enter to send, Shift+Enter for newline)"
            rows={2}
            data-testid="dd-followup-input"
            disabled={sending}
            className="flex-1 bg-[#050505] border border-white/15 px-4 py-3 font-mono text-sm text-white placeholder-zinc-600 focus:border-[#00ff88] focus:outline-none resize-none"
          />
          <button
            type="submit"
            disabled={!followUp.trim() || sending}
            data-testid="dd-send-button"
            className="inline-flex items-center gap-2 bg-[#00ff88] text-[#050505] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors h-fit"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </div>
      </form>
    </div>
  );
}
